import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Section, Eyebrow, Headline, Lead } from '../../components/iss/IssPrimitives';
import FileDrop from '../../components/FileDrop';
import { TEMPLATE_FILES, documentLinkProps, templateHref } from '../templateFile';
import apiService from '../../service/ApiService';
import '../initiation/PocTypePage.scss';

type LocationState = {
  pocId?: string;
  pocType?: string;
  businessJustification?: Record<string, unknown>;
  scorecard?: Record<string, unknown>;
  scorecardLockMode?: 'SAVE' | 'SAVE_SUBMIT';
  facilitationSupport?: boolean;
  successCriteria?: {
    criterion: string;
    measurement: string;
  }[];
  environment?: {
    siteType?: string;
    testSiteAvailable?: boolean | 'NOT_NEEDED';
  };
  nda?: {
    sharingConfidentialInfo: boolean;
    signedNdaFileName: string | null;
    confirmed: boolean;
  };
};

const NDA_TEMPLATE_LABEL =
  '01_ Confidentiality Undertaking for Suppliers - Mutual (ISS World Services)';

const NDA_TEMPLATE_HREF = templateHref(TEMPLATE_FILES.nda);

const NdaAssessmentPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as LocationState | null) ?? {};
  const pocId = state.pocId;

  const [sharingConfidentialInfo, setSharingConfidentialInfo] = useState<
    boolean | null
  >(
    typeof state.nda?.sharingConfidentialInfo === 'boolean'
      ? state.nda.sharingConfidentialInfo
      : null
  );
  const [signedNdaFileName, setSignedNdaFileName] = useState<string | null>(
    state.nda?.signedNdaFileName ?? null
  );
  const [signedNdaFile, setSignedNdaFile] = useState<File | null>(null);
  const [confirmed, setConfirmed] = useState(state.nda?.confirmed ?? false);
  const [attempted, setAttempted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const ndaRequired = sharingConfidentialInfo === true;

  const errors = useMemo(() => {
    const next: Record<string, string> = {};
    if (sharingConfidentialInfo === null) {
      next.sharing = 'Select Yes or No.';
    }
    if (ndaRequired && !signedNdaFileName) {
      next.file = 'Upload the signed NDA before continuing.';
    }
    if (!confirmed) {
      next.confirmed = 'Confirm that the information is truthful.';
    }
    return next;
  }, [sharingConfidentialInfo, ndaRequired, signedNdaFileName, confirmed]);

  const isValid = Object.keys(errors).length === 0;

  const handleNext = async () => {
    setAttempted(true);
    setSubmitError(null);
    if (!isValid) return;
  
    if (!pocId) {
      setSubmitError('Missing PoC id. Complete Initiation first.');
      return;
    }
    if (sharingConfidentialInfo === null) return;
  
    setIsSubmitting(true);
    try {
      let signedNdaFileRef: string | null = null;
  
      if (ndaRequired) {
        if (!signedNdaFile) {
          setSubmitError('Upload the signed NDA before continuing.');
          setIsSubmitting(false);
          return;
        }
        const uploaded = await apiService.uploadDocument(
          pocId,
          'SIGNED_NDA',
          signedNdaFile
        );
        signedNdaFileRef = uploaded.fileRef;
      }
  
      await apiService.saveNda(pocId, {
        sharingConfidentialInfo: sharingConfidentialInfo === true,
        signedNdaFileRef,
        confirmed,
      });
  
      navigate('/pocs/new/preparation/data-protection', {
        state: {
          ...state,
          nda: {
            sharingConfidentialInfo: sharingConfidentialInfo === true,
            signedNdaFileName: ndaRequired ? signedNdaFileName : null,
            signedNdaFileRef,
            confirmed,
          },
        },
      });
    } catch (err) {
      console.error(err);
      setSubmitError(
        'Could not save NDA. Check Network tab (upload + PUT nda).'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setSignedNdaFile(file);
    setSignedNdaFileName(file ? file.name : null);
  };

  return (
    <div className="initiation">
      <aside className="initiation__phases" aria-label="PoC phases">
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">1</span>
          <span>Initiation</span>
        </div>
        <div className="initiation__phase initiation__phase--active">
          <span className="initiation__phase-num">2</span>
          <span>Preparation</span>
        </div>
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">3</span>
          <span>Running</span>
        </div>
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">4</span>
          <span>Evaluation</span>
        </div>
      </aside>

      <div className="initiation__main">
        <nav className="initiation__steps" aria-label="Preparation steps">
          <span className="initiation__step">Step 1</span>
          <span className="initiation__step">Step 2</span>
          <span className="initiation__step initiation__step--active">
            Step 3
          </span>
          <span className="initiation__step">Step 4</span>
          <span className="initiation__step">Step 5</span>
          <span className="initiation__step">Step 6</span>
          <span className="initiation__step">Step 7</span>
          <span className="initiation__step">Review and Submit</span>
        </nav>

        <Section theme="light" spacing="tight">
          <Eyebrow>POC Preparation</Eyebrow>
          <Headline as="h1" size="3xl">
            Non-Disclosure Agreement
          </Headline>
          <Lead>Step 3 — NDA assessment</Lead>

          <p className="initiation__note">
            Please note that a Non-Disclosure Agreement (NDA) needs to be signed
            with the supplier before you initiate any dialogue with them.
          </p>

          <form
            className="bj-form"
            onSubmit={(event) => {
              event.preventDefault();
              handleNext();
            }}
          >
            <fieldset className="bj-field">
              <legend className="bj-field__label">
                Are you sharing any confidential information to the supplier?
              </legend>
              <p className="initiation__note">
                If you share any confidential information of ISS prior to
                signing a PoC agreement with the supplier, then it is
                recommended to sign an NDA. Confidential information includes,
                by way of example, any sensitive commercial, operational,
                technical, and/or strategic information of ISS, including of
                our customers, services/products or our suppliers, as well as
                any personal data.
              </p>
              <div className="bj-choice-list">
                <label className="bj-choice">
                  <input
                    type="radio"
                    name="nda-sharing"
                    checked={sharingConfidentialInfo === true}
                    onChange={() => {
                      setSharingConfidentialInfo(true);
                    }}
                  />
                  <span>
                    <strong>Yes</strong>
                  </span>
                </label>
                <label className="bj-choice">
                  <input
                    type="radio"
                    name="nda-sharing"
                    checked={sharingConfidentialInfo === false}
                    onChange={() => {
                      setSharingConfidentialInfo(false);
                      setSignedNdaFileName(null);
                      setSignedNdaFile(null);
                    }}
                  />
                  <span>
                    <strong>No</strong>
                  </span>
                </label>
              </div>
              {attempted && errors.sharing ? (
                <span className="bj-field__error">{errors.sharing}</span>
              ) : null}
            </fieldset>

            {sharingConfidentialInfo === false ? (
              <p className="scorecard__notice" role="status">
                No NDA is required. Your answer is recorded for data-capturing
                purposes only.
              </p>
            ) : null}

            {ndaRequired ? (
              <div className="phase-gate">
                <p className="phase-gate__title">NDA required</p>
                <p>
                  Download the mutual NDA template, fill it out (contact local
                  Legal if changes are needed), then upload the signed version.
                </p>
                <p>
                  <a
                    className="scorecard__link"
                    {...documentLinkProps(NDA_TEMPLATE_HREF)}
                  >
                    {NDA_TEMPLATE_LABEL}
                  </a>
                </p>

                <label className="bj-field">
                  <span className="bj-field__label">
                    Upload signed NDA (pdf/docx)
                  </span>
                  <FileDrop
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    hint=".pdf or .docx, up to 10 MB"
                    fileName={signedNdaFileName}
                    onChange={onFileChange}
                  />
                  {attempted && errors.file ? (
                    <span className="bj-field__error">{errors.file}</span>
                  ) : null}
                </label>
              </div>
            ) : null}

            <label className="score-result__confirm">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(event) => setConfirmed(event.target.checked)}
              />
              <span>
                I confirm that the provided information is truthful and aligned
                with ISS policy.
              </span>
            </label>
            {attempted && errors.confirmed ? (
              <p className="bj-field__error">{errors.confirmed}</p>
            ) : null}

            {submitError ? (
              <p className="bj-field__error">{submitError}</p>
            ) : null}

            <div className="initiation__actions">
              <button
                type="button"
                className="initiation__link-back"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                onClick={() =>
                  navigate('/pocs/new/preparation/environment', {
                    state: {
                      ...state,
                      nda: {
                        sharingConfidentialInfo:
                          sharingConfidentialInfo === true,
                        signedNdaFileName: ndaRequired
                          ? signedNdaFileName
                          : null,
                        confirmed,
                      },
                    },
                  })
                }
              >
                ← Back to PoC site
              </button>

              <button
                type="submit"
                className="initiation__next"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Next →'}
              </button>
            </div>
          </form>
        </Section>
      </div>
    </div>
  );
};

export default NdaAssessmentPage;
